/**
 * BrowserNotificationConsent — dismissible banner prompting the user
 * to enable browser notifications. Only shows when permission is 'default'
 * (not yet decided) and the banner hasn't been dismissed recently.
 * Renders null (no DOM) when not visible.
 */

import { useState, useEffect } from 'react'
import { Bell, X } from 'lucide-react'
import { useNotificationStore } from '../../stores/notifications'

const DISMISS_KEY = 'opsnest-notif-consent-dismissed'
const DISMISS_DAYS = 7

export default function BrowserNotificationConsent() {
  const { permission, requestPermission } = useNotificationStore()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (permission !== 'default') return
    // Check if dismissed recently
    const dismissed = localStorage.getItem(DISMISS_KEY)
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10)
      const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24)
      if (daysSince < DISMISS_DAYS) return
    }
    // Delay showing to avoid flashing on page load
    const timer = setTimeout(() => setVisible(true), 2000)
    return () => clearTimeout(timer)
  }, [permission])

  if (!visible) return null

  const handleEnable = async () => {
    await requestPermission()
    setVisible(false)
  }

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()))
    setVisible(false)
  }

  return (
    <div className="fixed bottom-16 right-4 z-[80] max-w-sm animate-in slide-in-from-bottom-4">
      <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <Bell size={16} className="text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900">Enable notifications?</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Get alerts for incoming calls, assigned tasks, and leads — even when you're on another tab.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={handleEnable}
                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg transition-colors"
              >
                Enable
              </button>
              <button
                onClick={handleDismiss}
                className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                Not now
              </button>
            </div>
          </div>
          <button onClick={handleDismiss} className="p-0.5 text-gray-400 hover:text-gray-600">
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  )
}
